import type { CSSProperties, ReactNode } from 'react';
import { ArrowRight, Check, Scale, ShieldAlert } from 'lucide-react';
import type { ArteBento } from '../dados/recursos';

/**
 * As cenas dos cartões do bento — FRAGMENTOS DA TELA REAL do produto.
 *
 * Quatro rodadas até aqui, e cada recusa do dono ensinou uma coisa:
 *   1. SVG de traço fino → "amador": linha solta em cartão grande é rascunho.
 *   2. miniaturas tímidas → o desenho boiava em vez de ocupar o quadro.
 *   3. pílulas flutuando → "os cards ficaram bons, as informações não": frase
 *      em português em ângulo aleatório lê como erro jogado na tela.
 *   4. tela real, mas pequena → o cartão de 1104px com um fragmento de 304px
 *      no meio era espaço morto. Agora são DOIS cartões por linha e a cena
 *      preenche o quadro.
 *
 * ## O neon carrega significado, e é isso que o impede de virar enfeite
 * Acende o que o SISTEMA produz (contrato consolidado, norma citada,
 * concordância conferida) em azul, e o que ele RECUSA em vermelho. O painel
 * do que a pessoa digitou fica neutro — a semântica do produto se aprende só
 * de olhar.
 *
 * O neon é o tubo aceso (`.tela-neon*`) MAIS o pulso que corre pela borda
 * (`.borda-pulso*`), as duas em `estilos.css`. O pulso foi pedido apontando a
 * referência dentro da própria página: é o mesmo gesto do feixe que desce do
 * "estado da sociedade" até os três documentos. Uma versão que acendia no
 * hover foi recusada — a luz CORRE sozinha, não espera o mouse. Os quatro
 * pulsos são defasados (`atraso`): em sincronia, virariam pisca.
 *
 * Regras da casa: `aria-hidden` sempre (o texto do cartão já diz o que a cena
 * mostra), toda cor de token, e conteúdo de domínio verdadeiro — cada defeito
 * aqui é uma regra que `packages/validador` de fato aplica.
 */

/** O palco: bloom da marca + grade de pontos, o mesmo em todas as cenas. */
function Palco({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden">
      <div className="absolute inset-0 [background:radial-gradient(65%_90%_at_50%_40%,color-mix(in_oklab,var(--color-auth-marca)_22%,transparent),transparent_72%)]" />
      <div className="absolute inset-0 opacity-60 [background-image:radial-gradient(circle_at_1px_1px,rgb(230_237_247/0.07)_1px,transparent_0)] [background-size:22px_22px] [mask-image:radial-gradient(70%_90%_at_50%_45%,black,transparent_78%)]" />
      {children}
    </div>
  );
}

/**
 * Painel de tela. `tom` decide o neon: `produz` (azul) para o que o sistema
 * gera, `recusa` (vermelho) para o que ele barra, `neutro` para o que a
 * pessoa digitou.
 */
function Tela({
  children,
  tom = 'neutro',
  atraso = 0,
  className = '',
}: {
  children: ReactNode;
  tom?: 'neutro' | 'produz' | 'recusa';
  /** Defasagem do pulso de borda — quatro cenas em sincronia viram pisca. */
  atraso?: number;
  className?: string;
}): JSX.Element {
  const brilho =
    tom === 'produz'
      ? 'tela-neon borda-pulso'
      : tom === 'recusa'
        ? 'tela-neon-erro borda-pulso-erro'
        : 'border-auth-borda/12 shadow-[0_16px_40px_-16px_rgb(0_0_0/0.85)]';
  return (
    <div
      className={`bg-auth-fundo-2/95 rounded-auth-controle border p-3 ${brilho} ${className}`}
      style={{ ['--atraso-pulso' as string]: `${String(atraso)}ms` } as CSSProperties}
    >
      {children}
    </div>
  );
}

/** Rótulo de seção dentro da tela — o mesmo caixa-alta dos mocks. */
function Rotulo({
  children,
  tom = 'neutro',
}: {
  children: ReactNode;
  tom?: 'neutro' | 'marca';
}): JSX.Element {
  return (
    <p
      className={`text-[8px] font-medium tracking-[0.14em] uppercase ${
        tom === 'marca' ? 'text-auth-marca-hover' : 'text-auth-texto-terciario'
      }`}
    >
      {children}
    </p>
  );
}

/** Campo do formulário, com a mensagem de erro embaixo quando há. */
function Campo({
  rotulo,
  valor,
  erro,
}: {
  rotulo: string;
  valor: string;
  erro?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-auth-texto-terciario text-[8px] font-medium tracking-[0.12em] uppercase">
        {rotulo}
      </span>
      <span
        className={`flex h-6 items-center rounded-md border px-2 text-[10px] tabular-nums ${
          erro
            ? 'border-auth-erro/60 bg-auth-erro/10 text-auth-erro'
            : 'border-auth-borda/12 bg-auth-superficie/[0.03] text-auth-texto'
        }`}
      >
        {valor}
      </span>
      {erro ? (
        <span className="text-auth-erro/90 text-[8px] leading-tight">{erro}</span>
      ) : null}
    </div>
  );
}

/**
 * O CADASTRO RECUSANDO na hora — a tela em que o contador digita, cada campo
 * inválido com o motivo embaixo, o rodapé somando o que não fecha, e o laudo
 * por cima. Três regras reais: `cpf-invalido`, `socio-sem-capacidade-civil`
 * e `quotas-nao-fecham-capital`.
 */
function ArteTrava(): JSX.Element {
  return (
    <Palco>
      <Tela className="w-[19rem] sm:w-[27rem]">
        <div className="mb-2 flex items-center justify-between">
          <Rotulo>Sócio 2 de 2</Rotulo>
          <span className="text-auth-texto-terciario flex items-center gap-1 text-[8px]">
            <span className="bg-auth-alerta size-1 rounded-full" />
            não salvo
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2">
          <Campo rotulo="Nome" valor="Ana Ribeiro" />
          <Campo rotulo="CPF" valor="111.111.111-11" erro="dígito verificador não bate" />
          <Campo rotulo="Quotas" valor="120" />
          <Campo rotulo="Nascimento" valor="12/03/2011" erro="15 anos na data do ato" />
        </div>
        <div className="border-auth-borda/12 mt-2.5 flex items-center justify-between border-t pt-2">
          <span className="text-auth-texto-terciario text-[9px] tabular-nums">
            Capital R$ 200.000,00 · 200 quotas
          </span>
          <span className="text-auth-erro text-[9px] font-medium tabular-nums">
            220 subscritas
          </span>
        </div>
      </Tela>

      {/* O laudo POR CIMA da tela: a recusa é imediata, não vem da Junta. */}
      <Tela
        tom="recusa"
        atraso={900}
        className="absolute right-2 bottom-2 flex items-center gap-2 py-1.5 sm:right-5 sm:bottom-4"
      >
        <ShieldAlert size={13} className="text-auth-erro shrink-0" />
        <span className="text-auth-erro text-[10px] font-semibold">Impede</span>
        <span className="text-auth-texto-secundario text-[10px]">3 achados</span>
      </Tela>
    </Palco>
  );
}

/** Linha de texto de documento — uma delas em destaque. */
function LinhaTexto({
  largura,
  destaque = false,
}: {
  largura: string;
  destaque?: boolean;
}): JSX.Element {
  return (
    <span
      className={`h-1.5 rounded-full ${destaque ? 'bg-auth-marca-hover/60' : 'bg-auth-texto/18'} ${largura}`}
    />
  );
}

/**
 * A CONSOLIDAÇÃO — o evento à esquerda, o contrato inteiro reimpresso à
 * direita. A cláusula alterada aparece destacada nos dois lados: é a mesma
 * origem, não uma cópia redigitada.
 */
function ArteConsolida(): JSX.Element {
  return (
    <Palco>
      <div className="flex items-center gap-2 sm:gap-3">
        <Tela className="w-[7.5rem] shrink-0 sm:w-[10rem]">
          <Rotulo>Alteração nº 3</Rotulo>
          <div className="mt-2 flex flex-col gap-1.5">
            <LinhaTexto largura="w-full" />
            <LinhaTexto largura="w-4/5" destaque />
            <LinhaTexto largura="w-3/5" />
          </div>
          <span className="text-auth-texto-terciario mt-2 block text-[8px]">
            sede alterada
          </span>
        </Tela>

        <div className="flex shrink-0 flex-col items-center gap-0.5">
          <ArrowRight size={13} className="text-auth-marca-hover" />
          <span className="text-auth-marca-hover text-[7px] font-medium tracking-[0.1em] uppercase">
            render
          </span>
        </div>

        <Tela tom="produz" atraso={1400} className="w-[9.5rem] shrink-0 sm:w-[13rem]">
          <Rotulo tom="marca">Contrato consolidado</Rotulo>
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-auth-texto-terciario text-[7px] tracking-[0.1em] uppercase">
                Cláusula primeira
              </span>
              <LinhaTexto largura="w-full" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-auth-marca-hover text-[7px] tracking-[0.1em] uppercase">
                Cláusula segunda — da sede
              </span>
              <LinhaTexto largura="w-11/12" destaque />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-auth-texto-terciario text-[7px] tracking-[0.1em] uppercase">
                Cláusula terceira
              </span>
              <LinhaTexto largura="w-4/5" />
            </div>
          </div>
          <div className="border-auth-borda/12 mt-2 flex items-center justify-between border-t pt-1.5">
            <span className="text-auth-texto-terciario text-[8px] tabular-nums">
              18 cláusulas
            </span>
            <span className="text-auth-marca-hover text-[8px]">do estado atual</span>
          </div>
        </Tela>
      </div>
    </Palco>
  );
}

/**
 * A CONCORDÂNCIA — o mesmo número, os dois gêneros. É a demonstração que
 * nenhum Word entrega: "a sócia … cento e uma" e "o sócio … cento e um"
 * saem da MESMA função (`packages/shared/src/redacao/genero.ts`), com o
 * algarismo idêntico.
 */
function ArteConcorda(): JSX.Element {
  return (
    <Palco>
      <Tela tom="produz" atraso={500} className="w-[19rem] sm:w-[26rem]">
        <Rotulo tom="marca">Cláusula quarta — do capital</Rotulo>
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-auth-texto-secundario text-[10px] leading-relaxed">
            <span className="text-auth-marca-hover font-medium">a sócia</span> Maria
            Ribeiro integraliza{' '}
            <span className="text-auth-texto font-semibold tabular-nums">101</span> (
            <span className="text-auth-marca-hover font-medium">cento e uma</span>) quotas
          </p>
          <p className="text-auth-texto-secundario text-[10px] leading-relaxed">
            <span className="text-auth-marca-hover font-medium">o sócio</span> João
            Ribeiro integraliza{' '}
            <span className="text-auth-texto font-semibold tabular-nums">101</span> (
            <span className="text-auth-marca-hover font-medium">cento e um</span>) quotas
          </p>
        </div>
        <div className="border-auth-borda/12 mt-2.5 flex flex-wrap items-center gap-1.5 border-t pt-2">
          {['gênero', 'número', 'extenso'].map((selo) => (
            <span
              key={selo}
              className="bg-auth-ok/15 text-auth-ok flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[8px] font-medium"
            >
              <Check size={8} strokeWidth={3} />
              {selo}
            </span>
          ))}
          <span className="text-auth-texto-terciario text-[8px]">
            conferem com o algarismo
          </span>
        </div>
      </Tela>
    </Palco>
  );
}

/**
 * O FUNDAMENTO — a cláusula com as normas que a sustentam, e a que CAIU com
 * a data. É o que impede o contrato de citar instrução normativa revogada.
 */
function ArteFundamento(): JSX.Element {
  return (
    <Palco>
      <Tela tom="produz" atraso={2100} className="w-[19rem] sm:w-[26rem]">
        <Rotulo tom="marca">Cláusula sétima — da administração</Rotulo>
        <div className="mt-2 flex flex-col gap-1.5">
          <LinhaTexto largura="w-full" />
          <LinhaTexto largura="w-5/6" />
        </div>
        <div className="mt-2.5 flex flex-col gap-1.5">
          {[
            { norma: 'art. 1.060, Código Civil', estado: 'vigente' },
            { norma: 'art. 997, VI, Código Civil', estado: 'vigente' },
          ].map((item) => (
            <span
              key={item.norma}
              className="border-auth-marca-hover/30 bg-auth-marca/10 text-auth-marca-hover flex items-center gap-1.5 rounded-md border px-2 py-1 text-[9px] font-medium"
            >
              <Scale size={10} className="shrink-0" />
              {item.norma}
              <span className="text-auth-ok ml-auto text-[8px] font-normal">
                {item.estado}
              </span>
            </span>
          ))}
          <span className="border-auth-borda/12 text-auth-texto-terciario flex items-center gap-1.5 rounded-md border px-2 py-1 text-[9px]">
            <span className="bg-auth-alerta/70 size-1.5 shrink-0 rounded-full" />
            <s>IN DREI 81/2020</s>
            <span className="ml-auto tabular-nums">revogada 05/2022</span>
          </span>
        </div>
      </Tela>
    </Palco>
  );
}

export const ARTES: Record<ArteBento, () => JSX.Element> = {
  trava: ArteTrava,
  consolida: ArteConsolida,
  concorda: ArteConcorda,
  fundamento: ArteFundamento,
};
